import { DocType } from '@/models/docs';

import { rangeLabel } from './fileLabels';
import { DraftSplit, makeKey } from './pageSplitPrefill';

/** A run of file pages, 1-based and inclusive. */
export interface PageSpan {
    start: number;
    end: number;
}

export interface Coverage {
    /** Pages in no split and not left out. */
    gaps: PageSpan[];
    /** Pages in more than one split. */
    overlaps: PageSpan[];
    /** Pages in at least one split. */
    coveredCount: number;
}

/** Sorts spans and joins any that touch or overlap. */
export function mergeSpans(spans: PageSpan[]): PageSpan[] {
    const merged: PageSpan[] = [];
    [...spans]
        .sort((a, b) => a.start - b.start || a.end - b.end)
        .forEach((span) => {
            const last = merged[merged.length - 1];
            if (last && span.start <= last.end + 1) {
                last.end = Math.max(last.end, span.end);
            } else {
                merged.push({ ...span });
            }
        });
    return merged;
}

/** The parts of `spans` that `remove` does not cover. */
export function subtractSpans(spans: PageSpan[], remove: PageSpan[]): PageSpan[] {
    const cuts = mergeSpans(remove);
    return mergeSpans(spans).flatMap((span) => {
        const pieces: PageSpan[] = [];
        let start = span.start;
        for (const cut of cuts) {
            if (cut.end < start || cut.start > span.end) continue;
            if (cut.start > start) pieces.push({ start, end: cut.start - 1 });
            start = Math.max(start, cut.end + 1);
        }
        if (start <= span.end) pieces.push({ start, end: span.end });
        return pieces;
    });
}

/** Whether two lists cover the same pages; touching ranges count as one. */
export function spansEqual(a: PageSpan[], b: PageSpan[]): boolean {
    const left = mergeSpans(a);
    const right = mergeSpans(b);
    return (
        left.length === right.length &&
        left.every((span, i) => span.start === right[i].start && span.end === right[i].end)
    );
}

/** The type code the document made from a split is stored under; a label outside the known types is stored as unknown. */
const storedDocType = (docType: string): string =>
    (Object.values(DocType) as string[]).includes(docType) ? docType : DocType.UNKNOWN_TYPE;

interface SavedSplitLike {
    startPage: number;
    endPage: number;
    docType: string;
    folder: string | null;
}

interface FileDocLike {
    startPage: number | null;
    endPage: number | null;
    type: string;
    folder?: string | null;
}

/** Whether the documents are exactly one per saved split, with the split's pages, type and folder. */
export function docsMatchSplits(splits: SavedSplitLike[], docs: FileDocLike[]): boolean {
    return (
        splits.length === docs.length &&
        splits.every((split) =>
            docs.some(
                (doc) =>
                    doc.startPage === split.startPage &&
                    doc.endPage === split.endPage &&
                    doc.type === storedDocType(split.docType) &&
                    (doc.folder ?? null) === (split.folder ?? null),
            ),
        )
    );
}

/**
 * Gaps and overlaps among the splits' pages. A gap is a page of the file that
 * is in no split and not left out; without `pageCount` no gap can be known.
 */
export function checkCoverage(
    splits: PageSpan[],
    pageCount: number | null,
    leftOut: PageSpan[] = [],
): Coverage {
    const covered = mergeSpans(
        splits.map((s) => ({
            start: s.start,
            end: pageCount === null ? s.end : Math.min(s.end, pageCount),
        })),
    );
    const coveredCount = covered.reduce((n, s) => n + s.end - s.start + 1, 0);

    const doubled: PageSpan[] = [];
    const ordered = [...splits].sort((a, b) => a.start - b.start);
    let widest = ordered[0];
    for (const span of ordered.slice(1)) {
        if (span.start <= widest.end) {
            doubled.push({ start: span.start, end: Math.min(span.end, widest.end) });
        }
        if (span.end > widest.end) widest = span;
    }

    const gaps =
        pageCount === null || splits.length === 0
            ? []
            : subtractSpans(subtractSpans([{ start: 1, end: pageCount }], covered), leftOut);
    return { gaps, overlaps: mergeSpans(doubled), coveredCount };
}

/** The left-out pages that no split covers; a split that grows over them takes them back. */
export const trimLeftOut = (leftOut: PageSpan[], splits: PageSpan[]): PageSpan[] =>
    subtractSpans(leftOut, splits);

export const formatSpan = ({ start, end }: PageSpan): string =>
    start === end ? `page ${start}` : `pages ${start}-${end}`;

export type SplitPatch = Partial<
    Pick<DraftSplit, 'startPage' | 'endPage' | 'docType' | 'folder' | 'remark'>
>;

/**
 * The one way a split's start, end or type changes: a person verified the old
 * pages and type, so any change to them clears the Verified tick.
 */
export function updateSplit(row: DraftSplit, patch: SplitPatch): DraftSplit {
    const changesVerifiedFields = (['startPage', 'endPage', 'docType'] as const).some(
        (field) => patch[field] !== undefined && patch[field] !== row[field],
    );
    return { ...row, ...patch, checked: changesVerifiedFields ? false : row.checked };
}

/** Applies `patch` to the row with `key`. */
export const updateSplitByKey = (rows: DraftSplit[], key: string, patch: SplitPatch): DraftSplit[] =>
    rows.map((row) => (row.key === key ? updateSplit(row, patch) : row));

function pageOf(value: string): number | null {
    const page = Number(value);
    return value.trim() !== '' && Number.isInteger(page) && page >= 1 ? page : null;
}

export interface FixOption {
    /** Key of the split this option changes. */
    key: string;
    field: 'startPage' | 'endPage';
    page: number;
    label: string;
}

export interface FixOffer {
    /** What is wrong between the two splits. */
    problem: string;
    options: FixOption[];
}

/** How to close the gap or overlap between split `index` and the one after it: move this split's end, or the next one's start; pages left out are not a gap. */
export function fixOffer(
    rows: DraftSplit[],
    index: number,
    pageCount: number | null,
    leftOut: PageSpan[] = [],
): FixOffer | null {
    const row = rows[index];
    const next = rows[index + 1];
    if (!row || !next) return null;
    const start = pageOf(row.startPage);
    const end = pageOf(row.endPage);
    const nextStart = pageOf(next.startPage);
    const nextEnd = pageOf(next.endPage);
    if (start === null || end === null || nextStart === null || nextEnd === null) return null;
    if (nextStart < start || nextStart === end + 1) return null;
    if (pageCount !== null && end + 1 > pageCount) return null;

    if (nextStart === start) return sameStartOffer(start, end, nextEnd, row, next);
    const gaps = subtractSpans([{ start: end + 1, end: nextStart - 1 }], leftOut);
    if (nextStart > end + 1 && gaps.length === 0) return null;
    const problem =
        nextStart > end + 1
            ? `${gaps.map(formatSpan).join(', ')} are in no document.`
            : `${rangeLabel(nextStart, nextEnd)} overlaps ${rangeLabel(start, end).toLowerCase()}.`;
    const options: FixOption[] = [];
    if (nextStart - 1 >= start) {
        options.push({
            key: row.key,
            field: 'endPage',
            page: nextStart - 1,
            label: `${rangeLabel(start, end)}: end at page ${nextStart - 1}`,
        });
    }
    if (end + 1 <= nextEnd) {
        options.push({
            key: next.key,
            field: 'startPage',
            page: end + 1,
            label: `${rangeLabel(nextStart, nextEnd)}: start at page ${end + 1}`,
        });
    }
    return { problem: problem.charAt(0).toUpperCase() + problem.slice(1), options };
}

/** Two splits that start on the same page: the one that ends first stays, the other moves to start after it. */
function sameStartOffer(
    start: number,
    end: number,
    nextEnd: number,
    row: DraftSplit,
    next: DraftSplit,
): FixOffer {
    const options: FixOption[] = [];
    if (end + 1 <= nextEnd) {
        options.push({
            key: next.key,
            field: 'startPage',
            page: end + 1,
            label: `${rangeLabel(start, nextEnd)}: start at page ${end + 1}`,
        });
    }
    if (nextEnd + 1 <= end) {
        options.push({
            key: row.key,
            field: 'startPage',
            page: nextEnd + 1,
            label: `${rangeLabel(start, end)}: start at page ${nextEnd + 1}`,
        });
    }
    return {
        problem: `${rangeLabel(start, end)} and ${rangeLabel(start, nextEnd).replace(/^Pages? /, '')} both start on page ${start}.`,
        options,
    };
}

/** How to close a gap that no split sits beside on one side: the first split starts at the gap's first page, or the last split ends at its last. */
export function edgeGapFixes(rows: DraftSplit[], gap: PageSpan): FixOption[] {
    const placed = rows.flatMap((row) => {
        const start = pageOf(row.startPage);
        const end = pageOf(row.endPage);
        return start !== null && end !== null && end >= start ? [{ row, start, end }] : [];
    });
    const options: FixOption[] = [];
    if (placed.every((p) => p.start > gap.end)) {
        const after = placed.find((p) => p.start === gap.end + 1);
        if (after) {
            options.push({
                key: after.row.key,
                field: 'startPage',
                page: gap.start,
                label: `${rangeLabel(after.start, after.end)}: start at page ${gap.start}`,
            });
        }
    }
    if (placed.every((p) => p.end < gap.start)) {
        const before = placed.find((p) => p.end === gap.start - 1);
        if (before) {
            options.push({
                key: before.row.key,
                field: 'endPage',
                page: gap.end,
                label: `${rangeLabel(before.start, before.end)}: end at page ${gap.end}`,
            });
        }
    }
    return options;
}

export const applyFix = (rows: DraftSplit[], option: FixOption): DraftSplit[] =>
    updateSplitByKey(rows, option.key, { [option.field]: String(option.page) });

export type DeleteTarget = 'previous' | 'next' | 'none';

/** List positions of the splits a deleted split's pages can go to: the one ending just before it and the one starting just after, by page order; list order when its own pages are blank. */
export function deleteNeighbours(
    rows: DraftSplit[],
    index: number,
): { previous: number | null; next: number | null } {
    const gone = rows[index];
    if (!gone) return { previous: null, next: null };
    const start = pageOf(gone.startPage);
    const end = pageOf(gone.endPage);
    const nearest = (score: (row: DraftSplit) => number | null, better: (a: number, b: number) => boolean) => {
        let found: number | null = null;
        let best = 0;
        for (let i = 0; i < rows.length; i++) {
            const value = i === index ? null : score(rows[i]);
            if (value !== null && (found === null || better(value, best))) {
                found = i;
                best = value;
            }
        }
        return found;
    };
    const previous =
        start === null
            ? index > 0 && end !== null
                ? index - 1
                : null
            : nearest((row) => {
                  const rowEnd = pageOf(row.endPage);
                  return rowEnd !== null && rowEnd < start ? rowEnd : null;
              }, (a, b) => a > b);
    const next =
        end === null
            ? index < rows.length - 1 && start !== null
                ? index + 1
                : null
            : nearest((row) => {
                  const rowStart = pageOf(row.startPage);
                  return rowStart !== null && rowStart > end ? rowStart : null;
              }, (a, b) => a < b);
    return { previous, next };
}

/** Removes split `index`; its pages go to the split before it, the split after it (by page order), or nowhere (a gap). */
export function deleteSplit(rows: DraftSplit[], index: number, target: DeleteTarget): DraftSplit[] {
    const gone = rows[index];
    if (!gone) return rows;
    const next = rows.filter((_, i) => i !== index);
    const { previous, next: following } = deleteNeighbours(rows, index);
    if (target === 'previous' && previous !== null && pageOf(gone.endPage) !== null) {
        return updateSplitByKey(next, rows[previous].key, { endPage: gone.endPage });
    }
    if (target === 'next' && following !== null && pageOf(gone.startPage) !== null) {
        return updateSplitByKey(next, rows[following].key, { startPage: gone.startPage });
    }
    return next;
}

/** Why Split here can't cut at `page`, or null when it can. */
export function splitHereBlocker(rows: DraftSplit[], page: number | null, pageCount: number | null): string | null {
    if (page === null) return 'Open a page in the preview first.';
    if (rows.length === 0 && page === 1) {
        return 'Page 1 starts the whole file. Use "Whole file is one document".';
    }
    if (rows.some((row) => pageOf(row.startPage) === page)) return `Page ${page} already starts a document.`;
    if (splitHere(rows, page, pageCount) === null) {
        return rows.length === 0
            ? `Page ${page} is outside the file.`
            : `Page ${page} is in no document, so there is nothing to cut.`;
    }
    return null;
}

/** Starts a new split at `page`, taking the rest of the split that holds it; null when no split can be cut there. */
export function splitHere(
    rows: DraftSplit[],
    page: number,
    pageCount: number | null,
): { rows: DraftSplit[]; key: string } | null {
    if (rows.length === 0) {
        if (pageCount === null || page < 1 || page > pageCount) return null;
        const first: DraftSplit = {
            key: makeKey(),
            startPage: '1',
            endPage: String(page - 1),
            docType: '',
            folder: '',
            remark: '',
            checked: false,
        };
        const second: DraftSplit = {
            ...first,
            key: makeKey(),
            startPage: String(page),
            endPage: String(pageCount),
        };
        return page === 1 ? { rows: [second], key: second.key } : { rows: [first, second], key: second.key };
    }
    const index = rows.findIndex((row) => {
        const start = pageOf(row.startPage);
        const end = pageOf(row.endPage);
        return start !== null && end !== null && start < page && page <= end;
    });
    if (index < 0) return null;
    const row = rows[index];
    const created: DraftSplit = {
        ...row,
        key: makeKey(),
        startPage: String(page),
        remark: '',
        checked: false,
    };
    const cut = updateSplit(row, { endPage: String(page - 1) });
    return {
        rows: [...rows.slice(0, index), cut, created, ...rows.slice(index + 1)],
        key: created.key,
    };
}
